import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Text,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  useToast,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
} from '@chakra-ui/react';
import { FaInfoCircle, FaCheck } from 'react-icons/fa';
import { 
  TextInput, 
  TextArea, 
  SelectInput, 
  PriceInput, 
  SubmitButton, 
  useFormValidation 
} from './FormComponents';
import ImageUploader from './ImageUploader';
import { getCategories, getBrands } from '../data/mockData';
import { Category, Brand } from '../types';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

interface PostFormData {
  title: string;
  description: string;
  categoryId: string;
  brandId: string;
  condition: string;
  price: number;
  location: string;
  tags: string;
}

const initialFormData: PostFormData = {
  title: '',
  description: '',
  categoryId: '',
  brandId: '',
  condition: '',
  price: 0,
  location: '',
  tags: '',
};

interface PostCreationFormProps {
  onSubmit: (formData: PostFormData, images: ImageFile[]) => Promise<void>;
  isLoading?: boolean;
  editMode?: boolean;
  initialData?: Partial<PostFormData>;
  initialImages?: ImageFile[];
}

const steps = [
  { title: 'Basic Info', description: 'Title, category, and details' },
  { title: 'Pricing', description: 'Set your price and condition' },
  { title: 'Images', description: 'Upload photos of your item' },
  { title: 'Review', description: 'Review and publish your listing' },
];

export const PostCreationForm: React.FC<PostCreationFormProps> = ({
  onSubmit,
  isLoading = false,
  editMode = false,
  initialData = {},
  initialImages = [],
}) => {
  const [formData, setFormData] = useState<PostFormData>({ ...initialFormData, ...initialData });
  const [images, setImages] = useState<ImageFile[]>(initialImages);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof PostFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof PostFormData, boolean>>>({});
  
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });
  
  const toast = useToast();
  const { 
    validateRequired, 
    validatePrice, 
    showValidationError, 
    showSuccess 
  } = useFormValidation();

  // Load categories and brands
  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, brandsData] = await Promise.all([
          getCategories(),
          getBrands(),
        ]);
        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (error) {
        console.error('Error loading form data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load categories and brands',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    };

    loadData();
  }, [toast]);

  const handleFieldChange = (field: keyof PostFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof PostFormData, string>> = {};
    
    switch (step) {
      case 0: // Basic Info
        const titleError = validateRequired(formData.title, 'Title');
        if (titleError) newErrors.title = titleError;
        
        const descError = validateRequired(formData.description, 'Description');
        if (descError) newErrors.description = descError;
        
        const categoryError = validateRequired(formData.categoryId, 'Category');
        if (categoryError) newErrors.categoryId = categoryError;
        
        const locationError = validateRequired(formData.location, 'Location');
        if (locationError) newErrors.location = locationError;
        break;
        
      case 1: // Pricing
        const priceError = validatePrice(formData.price);
        if (priceError) newErrors.price = priceError;
        if (formData.price <= 0) newErrors.price = 'Price must be greater than $0';
        
        const conditionError = validateRequired(formData.condition, 'Condition');
        if (conditionError) newErrors.condition = conditionError;
        break;
        
      case 2: // Images
        if (images.length === 0) {
          toast({
            title: 'Images Required',
            description: 'Please upload at least one image of your item',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return false;
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    } else {
      showValidationError('Please fix the errors before continuing');
    }
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    // Validate all steps
    let isValid = true;
    for (let i = 0; i < 3; i++) {
      if (!validateStep(i)) {
        isValid = false;
      }
    }
    
    if (!isValid) {
      showValidationError('Please fix all errors before submitting');
      return;
    }

    try {
      await onSubmit(formData, images);
      showSuccess(editMode ? 'Listing updated successfully!' : 'Listing created successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      showValidationError('Failed to submit listing. Please try again.');
    }
  };

  const conditionOptions = [
    { value: 'excellent', label: 'Excellent - Like new, no visible wear' },
    { value: 'like-new', label: 'Like New - Minor signs of use' },
    { value: 'good', label: 'Good - Some wear but functions perfectly' },
    { value: 'fair', label: 'Fair - Noticeable wear but still functional' },
    { value: 'poor', label: 'Poor - Significant wear, may need repairs' },
  ];

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name,
  }));

  const brandOptions = brands.map(brand => ({
    value: brand.id,
    label: brand.name,
  }));

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'green';
      case 'like-new': return 'blue';
      case 'good': return 'yellow';
      case 'fair': return 'orange';
      case 'poor': return 'red';
      default: return 'gray';
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" color="brand.green.dark">
              Tell us about your item
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <TextInput
                  name="title"
                  label="Item Title"
                  placeholder="e.g., Wilson Pro Staff Tennis Racket - Grip Size 4 3/8"
                  value={formData.title}
                  onChange={(value) => handleFieldChange('title', value)}
                  errorMessage={touched.title ? errors.title : undefined}
                  isRequired
                  helperText="Create a clear, descriptive title that buyers will search for"
                />
              </GridItem>
              
              <GridItem>
                <SelectInput
                  name="categoryId"
                  label="Category"
                  placeholder="Select a category"
                  options={categoryOptions}
                  value={formData.categoryId}
                  onChange={(value) => handleFieldChange('categoryId', value)}
                  errorMessage={touched.categoryId ? errors.categoryId : undefined}
                  isRequired
                />
              </GridItem>
              
              <GridItem>
                <SelectInput
                  name="brandId"
                  label="Brand"
                  placeholder="Select a brand (optional)"
                  options={brandOptions}
                  value={formData.brandId}
                  onChange={(value) => handleFieldChange('brandId', value)}
                  helperText="Help buyers find your item faster"
                />
              </GridItem>
              
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <TextArea
                  name="description"
                  label="Description"
                  placeholder="Describe the tennis equipment's features, condition, grip size, string tension, and any important details..."
                  value={formData.description}
                  onChange={(value) => handleFieldChange('description', value)}
                  errorMessage={touched.description ? errors.description : undefined}
                  isRequired
                  rows={6}
                  maxLength={2000}
                  helperText="Be detailed and honest about the condition and features"
                />
              </GridItem>
              
              <GridItem>
                <TextInput
                  name="location"
                  label="Location"
                  placeholder="e.g., San Francisco, CA"
                  value={formData.location}
                  onChange={(value) => handleFieldChange('location', value)}
                  errorMessage={touched.location ? errors.location : undefined}
                  isRequired
                  helperText="City and state for local pickup"
                />
              </GridItem>
              
              <GridItem>
                <TextInput
                  name="tags"
                  label="Tags"
                  placeholder="e.g., tennis, racket, wilson, pro-staff, grip-4-3/8"
                  value={formData.tags}
                  onChange={(value) => handleFieldChange('tags', value)}
                  helperText="Comma-separated tags to help buyers find your item"
                />
              </GridItem>
            </Grid>
          </VStack>
        );
        
      case 1:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" color="brand.green.dark">
              Pricing and Condition
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              <GridItem>
                <PriceInput
                  name="price"
                  label="Price"
                  value={formData.price}
                  onChange={(value) => handleFieldChange('price', value)}
                  errorMessage={touched.price ? errors.price : undefined}
                  isRequired
                  helperText="Set a competitive price to attract buyers"
                  min={1}
                  max={10000}
                />
              </GridItem>
              
              <GridItem>
                <SelectInput
                  name="condition"
                  label="Condition"
                  placeholder="Select condition"
                  options={conditionOptions}
                  value={formData.condition}
                  onChange={(value) => handleFieldChange('condition', value)}
                  errorMessage={touched.condition ? errors.condition : undefined}
                  isRequired
                />
              </GridItem>
            </Grid>
            
            {formData.condition && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">
                    <Badge colorScheme={getConditionColor(formData.condition)} mr={2}>
                      {formData.condition.toUpperCase()}
                    </Badge>
                    Condition Guidelines
                  </Text>
                  <AlertDescription mt={2}>
                    {conditionOptions.find(opt => opt.value === formData.condition)?.label.split(' - ')[1]}
                  </AlertDescription>
                </Box>
              </Alert>
            )}
            
            <Box bg="brand.gray.50" p={4} borderRadius="md">
              <Text fontSize="sm" color="brand.gray.600" mb={2}>
                💡 Pricing Tips:
              </Text>
              <VStack align="start" spacing={1} fontSize="sm" color="brand.gray.600">
                <Text>• Research similar items to set a competitive price</Text>
                <Text>• Consider the item's age, condition, and original retail price</Text>
                <Text>• Leave room for negotiation if you're open to offers</Text>
                <Text>• Include shipping costs in your price or specify pickup only</Text>
              </VStack>
            </Box>
          </VStack>
        );
        
      case 2:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" color="brand.green.dark">
              Upload Images
            </Heading>
            
            <ImageUploader
              images={images}
              onImagesChange={setImages}
              maxFiles={6}
              maxFileSize={10}
              required
              helperText="Add multiple angles and close-ups to showcase your item's condition"
            />
            
            <Box bg="brand.gray.50" p={4} borderRadius="md">
              <Text fontSize="sm" color="brand.gray.600" mb={2}>
                📸 Photo Tips:
              </Text>
              <VStack align="start" spacing={1} fontSize="sm" color="brand.gray.600">
                <Text>• Use good lighting - natural light works best</Text>
                <Text>• Show the item from multiple angles</Text>
                <Text>• Include close-ups of any wear or damage</Text>
                <Text>• Keep backgrounds clean and uncluttered</Text>
                <Text>• The first image will be your main listing photo</Text>
              </VStack>
            </Box>
          </VStack>
        );
        
      case 3:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" color="brand.green.dark">
              Review Your Listing
            </Heading>
            
            <Card>
              <CardHeader>
                <HStack>
                  <FaInfoCircle color="var(--chakra-colors-brand-green-medium)" />
                  <Text fontWeight="semibold">Listing Preview</Text>
                </HStack>
              </CardHeader>
              <CardBody>
                <Grid templateColumns={{ base: '1fr', md: '200px 1fr' }} gap={6}>
                  <GridItem>
                    {images.length > 0 ? (
                      <Box
                        borderRadius="lg"
                        overflow="hidden"
                        border="2px solid"
                        borderColor="brand.primary"
                      >
                        <img
                          src={images[0].preview}
                          alt="Main"
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                          }}
                        />
                        {images.length > 1 && (
                          <Badge
                            position="absolute"
                            bottom={2}
                            right={2}
                            bg="rgba(0,0,0,0.8)"
                            color="white"
                            fontSize="xs"
                          >
                            +{images.length - 1} more
                          </Badge>
                        )}
                      </Box>
                    ) : (
                      <Box
                        h="200px"
                        bg="brand.gray.100"
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="brand.gray.400">No images</Text>
                      </Box>
                    )}
                  </GridItem>
                  
                  <GridItem>
                    <VStack align="start" spacing={3}>
                      <Text fontSize="xl" fontWeight="bold" color="brand.green.dark">
                        {formData.title || 'Untitled Item'}
                      </Text>
                      
                      <Text fontSize="2xl" fontWeight="bold" color="brand.primary">
                        ${formData.price.toFixed(2)}
                      </Text>
                      
                      <HStack spacing={4} flexWrap="wrap">
                        {formData.condition && (
                          <Badge colorScheme={getConditionColor(formData.condition)}>
                            {formData.condition.toUpperCase()}
                          </Badge>
                        )}
                        {formData.categoryId && (
                          <Text fontSize="sm" color="brand.gray.600">
                            {categories.find(c => c.id === formData.categoryId)?.name}
                          </Text>
                        )}
                        {formData.brandId && (
                          <Text fontSize="sm" color="brand.gray.600">
                            {brands.find(b => b.id === formData.brandId)?.name}
                          </Text>
                        )}
                      </HStack>
                      
                      <Text color="brand.gray.600" noOfLines={4}>
                        {formData.description || 'No description provided'}
                      </Text>
                      
                      <Text fontSize="sm" color="brand.gray.500">
                        📍 {formData.location || 'Location not specified'}
                      </Text>
                    </VStack>
                  </GridItem>
                </Grid>
              </CardBody>
            </Card>
            
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Ready to publish!</Text>
                <AlertDescription>
                  Your listing looks great. Click "Publish Listing" to make it live on ReDeuce.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        );
        
      default:
        return null;
    }
  };

  return (
    <Container maxW="4xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" color="brand.green.dark" mb={4}>
            {editMode ? 'Edit Your Listing' : 'Create New Listing'}
          </Heading>
          <Text color="brand.gray.600">
            {editMode ? 'Update your item details' : 'List your tennis equipment and gear for sale'}
          </Text>
        </Box>

        {/* Progress Stepper */}
        <Box py={4}>
          <Stepper index={activeStep} colorScheme="green">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink="0">
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider />

        {/* Step Content */}
        <Box minH="600px">
          {renderStepContent()}
        </Box>

        {/* Navigation */}
        <HStack justify="space-between" pt={4}>
          <Button
            onClick={handlePrevious}
            isDisabled={activeStep === 0}
            variant="outline"
            colorScheme="green"
          >
            Previous
          </Button>
          
          <HStack spacing={2}>
            <Text fontSize="sm" color="brand.gray.500">
              Step {activeStep + 1} of {steps.length}
            </Text>
          </HStack>
          
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              bg="brand.primary"
              color="black"
              _hover={{ bg: 'brand.primaryHover' }}
            >
              Next
            </Button>
          ) : (
            <SubmitButton
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText={editMode ? 'Updating...' : 'Publishing...'}
              width="auto"
            >
              <HStack spacing={2}>
                <FaCheck />
                <Text>{editMode ? 'Update Listing' : 'Publish Listing'}</Text>
              </HStack>
            </SubmitButton>
          )}
        </HStack>
      </VStack>
    </Container>
  );
};

export default PostCreationForm; 